import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getWorkDomainFromEmail } from '@gitbook/app-database';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';
import { GitBookLoader, toast } from '@gitbook/spine-react';

import { type NavigationTarget, useNavigation } from '../../navigation';
import {
    apiClient,
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
    useLoggedInUser,
} from '../../state';
import { SiteOnboardingScreen } from './SiteOnboardingScreen';
import { FLOW_STEPS, type PendingOrganization, useFlowContext } from './useFlowContext';
import { WelcomeLayout } from './WelcomeLayout';
import { default as WelcomeScreen } from './WelcomeScreen';

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function OnboardingFlow(props: {
    screen: NavigationScreenWelcome;
}): React.ReactElement | null {
    const { screen } = props;
    const [state, setStep] = useFlowContext();
    const user = useLoggedInUser();
    const [draftTitle, setDraftTitle] = React.useState<string | undefined>();

    let redirectTo: NavigationTarget | null = null;
    let title: string | undefined;
    let content: React.ReactNode = null;

    switch (state.step) {
        case FLOW_STEPS.CREATE_ORGANIZATION:
            title = draftTitle;
            content = (
                <WelcomeScreen
                    screen={screen}
                    onNext={({ organization, siteId, hasBootstrappedContent }) => {
                        setStep({
                            step: FLOW_STEPS.COMPLETE,
                            context: { organization, siteId, hasBootstrappedContent },
                        });
                    }}
                    onNextSiteOnboarding={(pendingOrganization) => {
                        setStep({
                            step: FLOW_STEPS.SITE_ONBOARDING,
                            context: { pendingOrganization },
                        });
                    }}
                    onTitleChange={setDraftTitle}
                />
            );
            break;

        case FLOW_STEPS.SITE_ONBOARDING:
            title = state.context.pendingOrganization.title;
            content = (
                <SiteOnboardingScreen
                    onCreateSite={(preferences) =>
                        createOrganizationAndSite({
                            pendingOrganization: state.context.pendingOrganization,
                            email: user.email,
                            preferences,
                        })
                    }
                    onGoToSite={(result) => {
                        setStep({ step: FLOW_STEPS.COMPLETE, context: result });
                    }}
                />
            );
            break;

        case FLOW_STEPS.COMPLETE:
            assertOrgAndSiteCreated(state.context);
            title = state.context.organization.title;

            redirectTo = {
                screen: {
                    type: 'site',
                    organization: state.context.organization.id,
                    site: state.context.siteId,
                },
            };
            content = <WelcomeLoader />;
            break;

        default:
            return null;
    }

    return (
        <WelcomeLayout title={title}>
            <WelcomeRedirect to={redirectTo} />
            <React.Suspense fallback={<WelcomeLoader />}>{content}</React.Suspense>
        </WelcomeLayout>
    );
}

/** Create the organization (with its default site) now that site onboarding is done. */
async function createOrganizationAndSite({
    pendingOrganization,
    email,
    preferences,
}: {
    pendingOrganization: PendingOrganization;
    email: string | null | undefined;
    preferences: api.SiteOnboardingPreferences;
}): Promise<{
    organization: api.Organization;
    siteId: string;
    hasBootstrappedContent?: boolean;
} | null> {
    const emailDomain = email ? getWorkDomainFromEmail(email) : null;

    return (
        (await toast.catch(async () => {
            const openapiOnboardingParam = getOnboardingParam(OnboardingParamName.OPENAPI);

            const { data: organization } = await apiClient.organizations.createOrganization(
                {
                    title: pendingOrganization.title,
                    emailDomains:
                        emailDomain && pendingOrganization.emailDomainEnabled ? [emailDomain] : [],
                    onboardingPreferences: preferences,
                },
                {
                    withDefaultSite: true,
                    ...(openapiOnboardingParam ? { openapiUrl: openapiOnboardingParam } : {}),
                }
            );

            const siteId =
                organization.defaultContent?.type === 'site'
                    ? organization.defaultContent.site
                    : undefined;

            if (!siteId) {
                throw new Error('Site must be created');
            }

            clearOnboardingParam(OnboardingParamName.OPENAPI);

            return {
                organization,
                siteId,
                hasBootstrappedContent: Boolean(openapiOnboardingParam),
            };
        }, 'Error while creating organization')) ?? null
    );
}

function assertOrgAndSiteCreated({
    organization,
    siteId,
}: {
    organization: api.Organization;
    siteId: string;
}) {
    if (!organization) {
        throw new Error('Could not create organization');
    }

    if (!siteId) {
        throw new Error('Site must be created');
    }
}

function WelcomeRedirect(props: { to: NavigationTarget | null }) {
    const { to } = props;
    const { navigate } = useNavigation();
    React.useLayoutEffect(() => {
        if (to) {
            navigate(to, { replace: true });
        }
    }, [navigate, to]);

    return null;
}

function WelcomeLoader() {
    return (
        <div className="flex min-h-80 w-full flex-col items-center justify-center gap-4">
            <GitBookLoader />
            <div className="text-ui-heading-small">Preparing your onboarding...</div>
        </div>
    );
}
